import * as React from "react";
import { SearchIcon, XIcon } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { useSearchQuery } from "../../services/api";
import {
  CATEGORY_META,
  MediaFlags,
  ResultSkeleton,
  SEARCH_PER_GROUP,
  TagBadge,
  formatDate,
  inspectionTitle,
  type SearchCategory,
  type SearchGroup,
} from "./search-shared";

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;
// Cap the whole dropdown to a compact preview across categories; the full set
// lives on the results page ("see all results").
const MAX_DROPDOWN_RESULTS = 8;

/**
 * Global search bar for the dashboard shell. The input anchors a Popover that
 * lists results grouped by category (capped to {@link MAX_DROPDOWN_RESULTS}),
 * with tag badges and image/video/sound indicators. Enter or "see more" opens
 * the full results page. Mirrors the anchor + debounce pattern of RemoteCombobox.
 *
 * Uses the shared {@link SEARCH_PER_GROUP} so this query and the results page's
 * (same q, no filters) share one RTK Query cache entry — pressing Enter
 * mid-search reuses the in-flight request instead of firing a duplicate.
 */
export function GlobalSearch() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const anchorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(text.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [text]);

  // This one bar is the app's search input (there's no field on the results
  // page). Keep it in sync with the results page's ?q= so a refresh / shared
  // link / re-search always shows the query that's on screen.
  const urlQuery =
    location.pathname === "/dashboard/search" ? searchParams.get("q") ?? "" : null;
  React.useEffect(() => {
    if (urlQuery !== null) setText(urlQuery);
  }, [urlQuery]);

  const active = debounced.length >= MIN_CHARS;
  const { data, isFetching } = useSearchQuery(
    { q: debounced, perGroup: SEARCH_PER_GROUP, lang: i18n.language },
    { skip: !active },
  );

  const groups: SearchGroup[] = data?.groups ?? [];
  const hasResults = groups.some((g) => g.results.length > 0);
  const totalResults = groups.reduce((sum, g) => sum + g.total, 0);

  // Trim the preview to at most MAX_DROPDOWN_RESULTS across all categories
  // (inspections first). The footer still reports the full total.
  let budget = MAX_DROPDOWN_RESULTS;
  const displayGroups = groups
    .map((g) => {
      const results = g.results.slice(0, Math.max(0, budget));
      budget -= results.length;
      return { ...g, results };
    })
    .filter((g) => g.results.length > 0);
  // The shown results are stale while the debounce is pending OR a fetch is in
  // flight. Track that as one signal so every keystroke gives feedback (a
  // spinner) instead of silently showing old data for a second or two.
  const trimmed = text.trim();
  const pending = trimmed.length >= MIN_CHARS && (trimmed !== debounced || isFetching);

  const openFull = () => {
    if (!text.trim()) return;
    setOpen(false);
    navigate(`/dashboard/search?q=${encodeURIComponent(text.trim())}`);
  };
  const openResult = (category: SearchCategory, id: SearchGroup["results"][number]["id"]) => {
    setOpen(false);
    navigate(CATEGORY_META[category].route(id));
  };
  const clear = () => {
    setText("");
    setDebounced("");
    setOpen(false);
    anchorRef.current?.querySelector("input")?.focus();
  };

  return (
    <Popover open={open && active} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative w-full max-w-xl">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") openFull();
            }}
            placeholder={t("search.placeholder")}
            className={cn("pl-8", text && "pr-8")}
            aria-label={t("search.placeholder")}
          />
          {text ? (
            <button
              type="button"
              onClick={clear}
              aria-label={t("common.clear")}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          ) : null}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        // Keep focus in the input so typing continues while the panel is open.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Treat the search input (the anchor) as part of the popover, so
        // re-focusing it reopens the cached results instead of the panel
        // flickering open and immediately closing.
        onInteractOutside={(e) => {
          const target = e.target as Node | null;
          if (target && anchorRef.current?.contains(target)) {
            e.preventDefault();
          }
        }}
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command shouldFilter={false}>
          {/* Grow with the available screen height instead of scrolling at 300px. */}
          <CommandList className="max-h-[calc(var(--radix-popover-content-available-height)_-_0.5rem)] py-2">
            {pending ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="px-3 py-2">
                  <ResultSkeleton />
                </div>
              ))
            ) : !hasResults ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t("search.no-results")}
              </div>
            ) : (
              <>
                {displayGroups.map((group) => (
                  <CommandGroup
                    key={group.category}
                    heading={t(CATEGORY_META[group.category].labelKey)}
                  >
                    {group.results.map((r) => (
                      <CommandItem
                        key={`${group.category}-${r.id}`}
                        value={`${group.category}-${r.id}`}
                        onSelect={() => openResult(group.category, r.id)}
                        className="flex cursor-pointer flex-col items-start gap-1"
                      >
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="truncate font-medium">
                            {group.category === "inspection" ? inspectionTitle(r) : r.title}
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            {group.category === "inspection" && r.date && (
                              <span className="text-xs text-muted-foreground">
                                {formatDate(r.date)}
                              </span>
                            )}
                            <MediaFlags media={r.media} />
                          </span>
                        </div>
                        {r.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {r.tags.slice(0, 3).map((tag) => (
                              <TagBadge key={tag.id} tag={tag} />
                            ))}
                          </div>
                        )}
                        {r.matchedItems?.[0] && (
                          <span className="line-clamp-1 text-xs text-muted-foreground">
                            <span className="font-medium">{r.matchedItems[0].title}</span>
                            {r.matchedItems[0].description
                              ? ` — ${r.matchedItems[0].description}`
                              : ""}
                          </span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
                <div className="border-t p-1">
                  <CommandItem
                    value="__see_more"
                    onSelect={openFull}
                    className="cursor-pointer justify-center text-sm font-medium text-primary"
                  >
                    {t("search.see-more")} ({totalResults})
                  </CommandItem>
                </div>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
